import { types, flow, applySnapshot, getSnapshot } from "mobx-state-tree";
import { PUBLISH_STATUSES } from "../constants/models";
import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";
import { MSURL } from "utils/ms";
import { isEmptyObject } from "../utils";
import { FILTER_COLUMNS } from "../constants/filterConstants";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";

const pathCoach = "/coach/api/v1";
export const CoachModel = types
  .model({
    id: types.number,
    title: types.string,
    email: types.string,
    description: types.maybeNull(types.string),
    photo: types.maybeNull(types.string),
    fasterwayCertifications: types.maybeNull(types.string),
    professionalCertifications: types.maybeNull(types.string),
    availableHours: types.maybeNull(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.photo,
      };
    },
  }));

const CoachStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathCoach}/coach`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: CoachModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(CoachModel),
    isMoreLoading: types.optional(types.boolean, false),
    isCoachLoading: types.optional(types.boolean, false),
    isCoachManaging: types.optional(types.boolean, false),
    isCoachPublishing: types.optional(types.boolean, false),
    isCoachCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateCoach(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreCoaches: flow(function*() {
      self.isCoachLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${pathCoach}/coach`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCoachLoading = false;
      }
    }),

    fetchCoachDetails: flow(function*(id?: number) {
      self.isCoachLoading = true;

      try {
        const details = yield api.get(
          `${pathCoach}/coach/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCoachLoading = false;
      }
    }),

    createCoach: flow(function*(payload: any) {
      self.isCoachManaging = true;

      try {
        const { id } = yield api.post(
          `${pathCoach}/coach`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCoachManaging = false;
      }
    }),

    editCoachInfo: flow(function*(payload: any) {
      self.isCoachManaging = true;

      try {
        return yield api.put(
          `${pathCoach}/coach/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCoachManaging = false;
      }
    }),

    editCoachPhoto: flow(function*(payload: any) {
      self.isCoachManaging = true;

      try {
        return yield api.putFormData(
          `${pathCoach}/coach/${payload.id}/upload-image`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCoachManaging = false;
      }
    }),

    changeCoachStatus: flow(function*(id: number, status: string) {
      self.isCoachPublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${pathCoach}/coach/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateCoach(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCoachPublishing = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default CoachStore.create();
